import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";

// Contexto de tema visual + tamanho da fonte das mensagens.
//
// Fonte da verdade = banco (user.darkMode / user.fontSizeMensagens), sincronizado
// pelo ThemeUserSync quando o usuário carrega. O localStorage é só um cache para
// aplicar o tema instantaneamente no boot (sem "flash") antes do user chegar.
//
// Aplica os efeitos colaterais:
//   - data-theme="dark|light" no <html> (dispara as CSS variables em theme.css);
//   - --msg-font-size no <html> (tamanho do corpo das mensagens).

const THEME_KEY = "app-theme";
const FONT_KEY = "msg-font-size";

export const FONT_SIZE_MIN = 8;
export const FONT_SIZE_MAX = 22;
export const FONT_SIZE_STEP = 2;
export const FONT_SIZE_DEFAULT = 14;

const clampFont = value => {
	const n = Number(value);
	if (!Number.isFinite(n)) return FONT_SIZE_DEFAULT;
	return Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(n)));
};

// Escolha inicial do tema: localStorage > preferência do SO > claro.
const getInitialDark = () => {
	const saved = localStorage.getItem(THEME_KEY);
	if (saved === "dark") return true;
	if (saved === "light") return false;
	if (window.matchMedia) {
		return window.matchMedia("(prefers-color-scheme: dark)").matches;
	}
	return false;
};

const getInitialFont = () => {
	const saved = localStorage.getItem(FONT_KEY);
	return saved ? clampFont(saved) : FONT_SIZE_DEFAULT;
};

export const ColorModeContext = createContext({
	darkMode: false,
	fontSize: FONT_SIZE_DEFAULT,
	setDarkMode: () => {},
	setFontSize: () => {},
});

export const ColorModeProvider = ({ children }) => {
	const [darkMode, setDarkModeState] = useState(getInitialDark);
	const [fontSize, setFontSizeState] = useState(getInitialFont);

	// Aplica o tema no <html> + persiste o cache local.
	useEffect(() => {
		document.documentElement.setAttribute(
			"data-theme",
			darkMode ? "dark" : "light"
		);
		localStorage.setItem(THEME_KEY, darkMode ? "dark" : "light");
	}, [darkMode]);

	// Aplica a variável de tamanho da fonte + persiste o cache local.
	useEffect(() => {
		document.documentElement.style.setProperty(
			"--msg-font-size",
			`${fontSize}px`
		);
		localStorage.setItem(FONT_KEY, String(fontSize));
	}, [fontSize]);

	const setDarkMode = value => setDarkModeState(!!value);
	const setFontSize = value => setFontSizeState(clampFont(value));

	const value = useMemo(
		() => ({ darkMode, fontSize, setDarkMode, setFontSize }),
		[darkMode, fontSize]
	);

	return (
		<ColorModeContext.Provider value={value}>
			{children}
		</ColorModeContext.Provider>
	);
};
